import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import { discoveryKeys, tagKeys } from '@/shared/lib/query/queryKeys';
import type { DomainTreeItem } from '@/shared/types/domain';

import { useDomainHierarchy } from '@/entities/domain';
import type { TagDetail } from '@/entities/tags';

import { updateTagDomainsApi } from '@/features/tags/api';

type UseTagDetailDomainEditParams = {
  tagName: string;
};

export const useTagDetailDomainEdit = ({ tagName }: UseTagDetailDomainEditParams) => {
  const queryClient = useQueryClient();
  const { data: hierarchyData } = useDomainHierarchy({ limit: 3000 });

  const tagData = queryClient.getQueryData<TagDetail>(tagKeys.tagDetail(tagName));
  const currentDomains: DomainTreeItem[] = useMemo(() => {
    if (!tagData) return [];
    return tagData.domains.map((domain) => {
      const { id, type, name, fullyQualifiedName, description, displayName, href } = domain;
      return {
        id,
        type,
        name,
        fullyQualifiedName,
        description,
        displayName,
        href,
      };
    });
  }, [tagData]);

  const updateDomainsMutation = useMutation({
    mutationFn: async (selectedDomainId: string | null) => {
      if (!hierarchyData?.data) {
        throw new Error(NOTIFICATION_MESSAGES.DOMAIN_TREE_UPDATE_ERROR);
      }
      await updateTagDomainsApi(tagName, selectedDomainId, hierarchyData.data, currentDomains);
    },
    onError: (_, _variables) => {
      notification.error({
        message: NOTIFICATION_MESSAGES.DOMAIN_TREE_UPDATE_ERROR,
        description: NOTIFICATION_MESSAGES.DOMAIN_TREE_UPDATE_ERROR_DESC,
        duration: 3,
        placement: 'bottomLeft',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.tagDetail(tagName) });
      queryClient.invalidateQueries({ queryKey: discoveryKeys.searchPrefix() });

      notification.success({
        message: NOTIFICATION_MESSAGES.DOMAIN_UPDATE_SUCCESS,
        description: NOTIFICATION_MESSAGES.DOMAIN_UPDATE_SUCCESS_DESC,
        placement: 'bottomLeft',
        duration: 3,
      });
    },
  });

  const handleSave = useCallback(
    async (selectedDomainIds: string[]) => {
      if (selectedDomainIds.length > 1) {
        notification.error({
          message: NOTIFICATION_MESSAGES.DOMAIN_TREE_ONLY_ONE_SELECT,
          description: NOTIFICATION_MESSAGES.DOMAIN_TREE_UPDATE_ERROR_DESC,
          duration: 5,
          placement: 'bottomLeft',
        });
        return;
      }

      const selectedDomainId = selectedDomainIds.length > 0 ? selectedDomainIds[0] : null;
      await updateDomainsMutation.mutateAsync(selectedDomainId);
    },
    [updateDomainsMutation]
  );

  return {
    handleSave,
    isUpdating: updateDomainsMutation.isPending,
  };
};
